<template>
    <div class="preview-container">
        <slot
            v-if="showCustomPreview"
            name="customPreview"
            :preview="generatedPreview"
        ></slot>
        <div
            v-else
            class="preview-step-container"
            :style="{'width': width + 'px', 'height': height + 'px'}"
        >
            <img
                v-if="previewCurrentStep === 0 && defaultImage"
                :src="defaultImage"
                class="preview-default-image"
            >
            <img
                v-else-if="previewImageUrl !== ''"
                :src="previewImageUrl"
                class="preview-default-image"
            >
            <component
                ref="stepper"
                :is="components"
                :is-in-stepper="isInStepper"
                :step-data="stepData"
                :preview="generatedPreview"
                :creation-request="request"
                :change-request="change"
                :count-down-duration="countDownDuration"
                :creation-request-params="creationRequestParams"
                :can-cancel-preview-generation="canCancelPreviewGeneration"
                :skip-last-step="skipLastStep"
                :preview-quota="previewQuota"
                :has-custom-preview-step="hasCustomPreviewStep"
                :error-verbose-status="errorVerboseStatus"
                :width="width"
                :request-creating="requestCreating"
                @changeStep="changeStep"
                @setPreviewData="setPreviewData"
                @reloadData="reloadData"
                @onCreationRequestCompleted="onCreationRequestCompleted"
                @onPreviewCreated="onPreviewCreated"
                @createNewPreview="createNewPreview(creationRequestParams)"
                @stopAndCreateNewPreview="createNewPreview(creationRequestParams, true)"
                @setToOriginalStep="setToOriginalStep()"
                @setErrorVerboseStatus="setErrorVerboseStatus"
            ></component>
        </div>
    </div>
</template>

<script>
    import Start from '@/components/Preview/PreviewStep/Start/';
    import Loading from '@/components/Preview/PreviewStep/Loading/';
    import Error from '@/components/Preview/PreviewStep/Error/';
    import Ready from '@/components/Preview/PreviewStep/Ready/';
    import Cancel from '@/components/Preview/PreviewStep/Cancel/';
    import Over from '@/components/Preview/PreviewStep/Over/';
    import Stop from '@/components/Preview/PreviewStep/Stop/';
    import Preview from '@/components/Preview/PreviewStep/Preview/';

    export default{
        name: 'Preview',

        props: {
            width: {
                default: 500,
                type: Number,
                required: false,
            },
            height: {
                default: 300,
                type: Number,
                required: false,
            },
            defaultImage: {
                type: String,
                required: false,
            },
            preview:{
                type: Object,
            },
            isInStepper:{
                type: Boolean,
                default: true,
            },
            creationRequest: {
                type: Object,
            },
            changeRequest: {
                type: Object,
            },
            creationRequestParams:{
                type: Object,
            },
            canCancelPreviewGeneration: {
                type: Boolean,
                default: true,
            },
            skipLastStep:{
                type: Boolean,
                default: false,
            },
            previewQuota: {
                type:Object
            },
            hasCustomPreviewStep:{
                type: Boolean,
                default: false,
            }
        },
        components: {
            Start,
            Loading,
            Error,
            Ready,
            Over,
            Cancel,
            Stop,
            Preview,
        },
        data(){
            return{
                loading: false,
                hasError: false,
                countDownDuration: 0,
                generatedPreview : {},
                previewImageUrl: '',
                previewAudio: '',
                displayPreview: false,
                showCustomPreview: false,
                creating: true,
                previewCurrentStep: 0,
                steps:[
                    {
                        iconColor: '#3F9EFF',
                        iconUrl: 'triangle.png',
                    },
                    {
                        name: 'loading',
                    },
                    {
                        iconColor: '#F56C6C',
                        iconUrl: 'cross.png',
                        stepTitle: 'Preview can’t be displayed',
                        linkText: 'Start new Preview'
                    },
                    {
                        iconColor: '#31BD9B',
                        iconUrl: 'checkmark.png',
                        stepTitle: 'Preview will start soon',
                    },
                    {
                        name: 'preview',
                    },
                    {
                        iconColor: '#3F9EFF',
                        iconUrl: 'triangle.png',
                        stepTitle: 'Your preview is over',
                        linkText: 'Start new Preview'
                    },
                    {
                        iconColor: '#3F9EFF',
                        iconUrl: 'triangle.png',
                        stepTitle: 'Preview canceled',
                        linkText: 'Start new Preview'
                    },
                    {
                        iconColor: '#3F9EFF',
                        iconUrl: 'triangle.png',
                        stepTitle: 'Preview stopped',
                        linkText: 'Start new Preview'
                    },
                ],
                request: undefined,
                change: undefined,
                errorVerboseStatus:'',
                requestCreating: false,
            };
        },
        mounted(){
            let hasRunningPreview = this.preview !== undefined && this.preview.status === 'RUNNING';
            let hasCreationRequest = this.creationRequest !== undefined && ['PENDING','RUNNING'].includes(this.creationRequest.status);
            let hasPendingPreview = this.preview !== undefined && this.preview.status === 'PENDING';
            let hasChangeRequest = this.changeRequest !== undefined && ['PENDING','RUNNING'].includes(this.changeRequest.status);

            if(hasCreationRequest){
                this.request = this.creationRequest;
                this.previewCurrentStep = 1;
            }else if(hasPendingPreview){
                this.previewCurrentStep = 3;
                this.generatedPreview = this.preview;
            }else if(hasRunningPreview){
                this.previewCurrentStep = 4;
                this.generatedPreview = this.preview;
            }
            else if(hasChangeRequest){
                this.change = this.changeRequest;
                this.previewCurrentStep = 4;
            }
        },
        watch:{
            /*
                When 2 user load the component and one of them start a preview
                we need to watch if a creation Request, a change Request or a preview
                is passed from parent and trigger a step change and a data update
                to prevent the second user to start a preview (wich will return an error)
            */
            creationRequest: function (newVal){
                if(newVal === undefined){
                    this.request = undefined;
                }else if(newVal){
                    this.request = newVal;
                    this.change = undefined;
                    this.previewCurrentStep = 1;
                }
            },
            'preview.uid': function (newVal){
                if(newVal === undefined){
                    newVal = {};
                    this.generatedPreview = newVal;
                }else if(newVal){
                    this.generatedPreview = this.preview;
                    this.request = undefined;
                    this.previewCurrentStep = 3;
                }
            },
            changeRequest: {
                handler: function (newVal){
                    if(newVal !== undefined){
                        this.change =  newVal;
                        this.previewCurrentStep = 4;
                    }
                },
                deep: true
            },
            previewCurrentStep: function(newVal){
                if(newVal === 4 && this.hasCustomPreviewStep){
                    this.showCustomPreview = true
                }
            }
        },
        computed : {
            stepForPreview (){
                return [Start, Loading, Error, Ready, Preview, Over, Cancel, Stop];
            },
            components(){
                return this.stepForPreview[this.previewCurrentStep];
            },
            stepData(){
                return this.steps[this.previewCurrentStep];
            },

        },
        methods: {
            createNewPreview(data, stop=false){
                this.startPreview(data.context, data.model, data.url,stop, data.scopes );
            },
            startPreview(context, requestModelName, requestModelUrl, stopPreview = false, requestScopes){
                this.requestCreating = true;
                return this.$refs.stepper.createPreviewRequest(context, requestModelName, requestModelUrl, stopPreview, requestScopes)
                .then(response =>{
                    this.$set(this, 'request', response);
                    this.requestCreating = false;
                    this.previewCurrentStep = 1;
                })
                .catch(()=>{
                    this.previewCurrentStep = 2;
                });
            },
            stopPreview(context, requestModelName, requestModelUrl){
                return this.$refs.stepper.createAndWatchRequest(context, requestModelName, requestModelUrl);
            },
            onCreationRequestCompleted(response){
                this.request = response;
            },
            onPreviewCreated(response){
                this.generatedPreview = response;
                this.previewCurrentStep = 4;
            },
            setPreviewData(data){
                this.previewImageUrl = data;
                this.displayPreview = true;
            },
            reloadData(){
                this.$emit('reloadData');
            },
            changeStep(data){
                if(data !== 4){
                    this.showCustomPreview=false;
                    this.previewImageUrl = '';
                }
                this.previewCurrentStep = data;
            },
            setToOriginalStep(){
                this.$emit('setToOriginalStep');
                this.previewCurrentStep = 0;
            },
            setErrorVerboseStatus(status){
                this.$set(this,'errorVerboseStatus', status)
            }
        },
    };
</script>

<style>
    .preview-container{
        display: inline-flex;
    }
    .preview-step-container{
        position: relative;
        display: block;
    }

    .preview-default-image{
        width: 100%;
        height: 100%;
    }

    .preview-indice{
        margin-top: 1rem;
        font-family: 'Montserrat';
        font-style: normal;
        font-weight: 700;
        font-size: 14px;
        line-height: 17px;
    }

    .loading-preview{
        font-size: 13px;
        text-align: center;
        color:  #ffffff;
    }

    .loading-title{
        margin-top: 1rem;
        font-family: 'Montserrat';
        font-style: normal;
        font-weight: 700;
        font-size: 14px;
        line-height: 17px;
    }

.preview-cancel{
    padding: 1rem;
    border: 1px solid #ffffff;
    background-color: transparent;
    color:  #ffffff;
    margin-top: 1rem;
}
    .preview-button{
        position: absolute;
        bottom: 0;
        left: 0;
        text-align: center;
        justify-content: center;
        align-items: center;
        height: 100%;
        width: 100%;
        flex-direction: column;
        display: flex;
        background-color: rgba(0,0,0,0.7);
    }

    .button{
        height: 40px;
        width: 40px;
        border-radius: 50%;
        border: none;
        color: #3F9EFF;
        font-size: 30px;
        background-color: #3F9EFF;
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 1rem;

    }
    .restart-button{
        background-color: #3F9EFF;
        border: none;
        color: white;
        border-radius: 2px;
        padding: 0.5rem 1rem;
        font-family: 'Montserrat';
        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 15px;
    }
    .step-title{
        margin-top: 1rem;
        font-family: 'Montserrat';
        font-style: normal;
        font-weight: 700;
        font-size: 14px;
        line-height: 17px;
    }
    .step-text{
        color: #FFFFFF;
    }
</style>


// WEBPACK FOOTER //
// src/components/Preview/index.vue